import React, { useMemo, useState } from "react";
import {
  FaCheckCircle,
  FaEnvelope,
  FaExclamationTriangle,
  FaEye,
  FaEyeSlash,
  FaLock,
  FaTimes,
  FaUserPlus,
  FaUserShield,
} from "react-icons/fa";

import { API_URL } from "../../config";

const UserForm = ({
  users = [],
  setUsers,
  currentRole,
}) => {
  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newRole, setNewRole] = useState("user");

  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const normalizedEmail = newEmail.trim().toLowerCase();

  const passwordStrength = useMemo(() => {
    let score = 0;

    if (newPassword.length >= 8) score += 1;
    if (newPassword.length >= 12) score += 1;
    if (/[A-Z]/.test(newPassword)) score += 1;
    if (/[a-z]/.test(newPassword)) score += 1;
    if (/\d/.test(newPassword)) score += 1;
    if (/[^A-Za-z0-9]/.test(newPassword)) score += 1;

    if (score <= 2) {
      return {
        label: "Schwach",
        className: "password-strength-weak",
        width: "33%",
      };
    }

    if (score <= 4) {
      return {
        label: "Mittel",
        className: "password-strength-medium",
        width: "66%",
      };
    }

    return {
      label: "Stark",
      className: "password-strength-strong",
      width: "100%",
    };
  }, [newPassword]);

  const emailExists = users.some(
    (user) =>
      String(user.email || "").trim().toLowerCase() === normalizedEmail
  );

  const showStatus = (type, text) => {
    setMessage({ type, text });
  };

  const resetForm = () => {
    setNewEmail("");
    setNewPassword("");
    setNewRole("user");
    setShowPassword(false);
  };

  const addUser = async (event) => {
    event.preventDefault();
    setMessage(null);

    if (!normalizedEmail || !newPassword) {
      showStatus("error", "Bitte E-Mail und Passwort eingeben.");
      return;
    }

    if (emailExists) {
      showStatus("error", "Ein Benutzer mit dieser E-Mail existiert bereits.");
      return;
    }

    if (newPassword.length < 10) {
      showStatus(
        "error",
        "Das Passwort muss mindestens 10 Zeichen lang sein."
      );
      return;
    }

    if (currentRole === "admin" && newRole !== "user") {
      showStatus(
        "error",
        "Admins dürfen nur Benutzer mit der Rolle Hausmeister anlegen."
      );
      return;
    }

    if (!["user", "admin", "superadmin"].includes(newRole)) {
      showStatus("error", "Die ausgewählte Rolle ist ungültig.");
      return;
    }

    setSaving(true);

    try {
      const response = await fetch(`${API_URL}/api/users`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: normalizedEmail,
          password: newPassword,
          role: newRole,
        }),
      });

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(
          data?.error || "Der Benutzer konnte nicht angelegt werden."
        );
      }

      const createdUser = {
        id: data?.userId ?? data?.id,
        email: data?.email ?? normalizedEmail,
        role: data?.role ?? newRole,
      };

      setUsers((previousUsers) => [
        ...previousUsers,
        createdUser,
      ]);

      resetForm();

      showStatus(
        "success",
        `Benutzer ${createdUser.email} wurde erfolgreich angelegt.`
      );
    } catch (error) {
      console.error("Benutzer konnte nicht angelegt werden:", error);

      showStatus(
        "error",
        error.message || "Der Benutzer konnte nicht angelegt werden."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="user-form-section">
      <div className="user-form-heading">
        <div className="user-form-heading-icon">
          <FaUserPlus />
        </div>

        <div>
          <span>Neues Konto</span>
          <h3>Benutzer hinzufügen</h3>
        </div>
      </div>

      <p className="user-form-description">
        Lege einen neuen Zugang für einen Hausmeister oder Administrator an.
      </p>

      {message && (
        <div
          className={`user-form-status user-form-status-${message.type}`}
          role="status"
        >
          {message.type === "success" ? (
            <FaCheckCircle />
          ) : (
            <FaExclamationTriangle />
          )}

          <span>{message.text}</span>

          <button
            type="button"
            aria-label="Meldung schließen"
            onClick={() => setMessage(null)}
          >
            <FaTimes />
          </button>
        </div>
      )}

      <form className="user-form" onSubmit={addUser}>
        <div className="user-form-field">
          <label htmlFor="new-user-email">
            E-Mail-Adresse
          </label>

          <div className="user-form-input-wrapper">
            <FaEnvelope />

            <input
              id="new-user-email"
              type="email"
              placeholder="hausmeister@beispiel.de"
              value={newEmail}
              onChange={(event) => setNewEmail(event.target.value)}
              autoComplete="off"
              disabled={saving}
              required
            />
          </div>

          {normalizedEmail && emailExists && (
            <span className="user-field-error">
              Diese E-Mail-Adresse ist bereits vergeben.
            </span>
          )}
        </div>

        <div className="user-form-field">
          <label htmlFor="new-user-password">
            Passwort
          </label>

          <div className="user-form-input-wrapper">
            <FaLock />

            <input
              id="new-user-password"
              type={showPassword ? "text" : "password"}
              placeholder="Mindestens 10 Zeichen"
              value={newPassword}
              onChange={(event) =>
                setNewPassword(event.target.value)
              }
              autoComplete="new-password"
              disabled={saving}
              required
            />

            <button
              type="button"
              className="user-password-toggle"
              onClick={() =>
                setShowPassword((current) => !current)
              }
              aria-label={
                showPassword
                  ? "Passwort verbergen"
                  : "Passwort anzeigen"
              }
            >
              {showPassword ? <FaEyeSlash /> : <FaEye />}
            </button>
          </div>

          {newPassword && (
            <div className="password-strength">
              <div className="password-strength-header">
                <span>Passwortstärke</span>
                <strong>{passwordStrength.label}</strong>
              </div>

              <div className="password-strength-track">
                <span
                  className={passwordStrength.className}
                  style={{ width: passwordStrength.width }}
                />
              </div>
            </div>
          )}

          <p className="password-hint">
            Mindestens 10 Zeichen, idealerweise mit Groß- und
            Kleinbuchstaben, Zahl und Sonderzeichen.
          </p>
        </div>

        <div className="user-form-field">
          <label htmlFor="new-user-role">
            Rolle
          </label>

          <div className="user-form-input-wrapper">
            <FaUserShield />

            <select
              id="new-user-role"
              value={newRole}
              onChange={(event) =>
                setNewRole(event.target.value)
              }
              disabled={saving}
            >
              <option value="user">
                Hausmeister
              </option>

              {currentRole === "superadmin" && (
                <>
                  <option value="admin">
                    Administrator
                  </option>

                  <option value="superadmin">
                    Superadministrator
                  </option>
                </>
              )}
            </select>
          </div>

          <p className="role-description">
            {getRoleDescription(newRole)}
          </p>
        </div>

        <button
          type="submit"
          className="add-user-button"
          disabled={saving || emailExists}
        >
          {saving ? (
            <>
              <span className="user-form-spinner" />
              Benutzer wird angelegt …
            </>
          ) : (
            <>
              <FaUserPlus />
              Benutzer hinzufügen
            </>
          )}
        </button>
      </form>
    </section>
  );
};

const getRoleDescription = (role) => {
  switch (role) {
    case "superadmin":
      return "Vollständiger Zugriff einschließlich Admin-Verwaltung.";
    case "admin":
      return "Kann Einträge und Hausmeisterkonten verwalten.";
    default:
      return "Kann QR-Codes scannen und eigene Einträge ansehen.";
  }
};

export default UserForm;